// src/pages/SettingsPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import apiClient from '../api/apiClient';
import ToggleSwitch from '../components/ToggleSwitch';
import Modal from '../components/Modal';
import WebhookModal from '../components/WebhookModal';
import ChangePasswordModal from '../components/ChangePasswordModal';
import {
    LuKey, LuWebhook, LuLogOut, LuBell, LuDownload,
    LuTrash2, LuCircleHelp, LuMail, LuInfo, LuChevronRight, LuSlidersHorizontal
} from "react-icons/lu";

// Reusable component for items that trigger an action (like opening a modal or navigating)
const SettingsItem = ({ icon, text, onClick }) => (
    <button onClick={onClick} className="w-full flex items-center justify-between p-3 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors text-left">
        <div className="flex items-center space-x-4">
            {icon}
            <span className="font-semibold">{text}</span>
        </div>
        <LuChevronRight size={18} className="text-slate-400" />
    </button>
);

const SettingsPage = () => {
    const { user } = useSelector((state) => state.auth);

    const [notifications, setNotifications] = useState(true);
    const [autoExit, setAutoExit] = useState(false);
    const [darkMode, setDarkMode] = useState(false);
    const [platformAlgoTrade, setPlatformAlgoTrade] = useState(false);
    const [personalAlgoTrade, setPersonalAlgoTrade] = useState(false);
    const [isWebhookModalOpen, setIsWebhookModalOpen] = useState(false);
    const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);

    const navigate = useNavigate();

    const handleLogout = () => {
        if (window.confirm("Are you sure you want to log out?")) {
            console.log("User logged out.");
            navigate('/login');
        }
    };

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await apiClient.get('/settings');
                const settings = response.data;
                setNotifications(settings.email_notifications || true);
                setAutoExit(settings.auto_square_off_enabled || false);
                setDarkMode(false); // Assuming no dark mode in backend yet
                setPlatformAlgoTrade(settings.platform_algo_trade_enabled || false);
                setPersonalAlgoTrade(settings.personal_algo_trade_enabled || false);
            } catch (error) {
                console.error('Error fetching settings:', error);
            }
        };

        if (user) {
            fetchSettings();
        }
    }, [user]);

    const updateSetting = async (key, value) => {
        try {
            await apiClient.put('/settings', { [key]: value });
        } catch (error) {
            console.error('Error updating setting:', error);
        }
    };

    const handlePlatformAlgoTradeToggle = (enabled) => {
        setPlatformAlgoTrade(enabled);
        updateSetting('platform_algo_trade_enabled', enabled);
    };

    if (!user) {
        return <div className="p-4 text-center text-red-600">User not authenticated. Please login again.</div>;
    }

    return (
        <div className="bg-slate-50 min-h-screen p-4 md:p-6 space-y-6 pb-24">

            {/* Page Header */}
            <h1 className="text-3xl font-bold text-slate-800">Settings</h1>

            {/* Profile Card */}
            <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200 flex items-center space-x-4">
                <img
                    // Using a placeholder image, you can replace this with a user's actual avatar
                    src={`https://api.dicebear.com/8.x/initials/svg?seed=${user?.name || 'User'}`}
                    alt="User Avatar"
                    className="h-16 w-16 rounded-full border-2 border-blue-200"
                />
                <div>
                    <p className="text-xl font-bold text-slate-800">{user?.name || 'User'}</p>
                    <p className="text-sm text-slate-500">{user?.email || 'user@email.com'}</p>
                </div>
            </div>

            {/* Quick Settings Card */}
            <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200 space-y-4">
                <h3 className="text-lg font-bold text-slate-800">Quick Settings</h3>
                <ToggleSwitch label="Trade Notifications" enabled={notifications} setEnabled={setNotifications} />
                <ToggleSwitch label="Auto Exit" enabled={autoExit} setEnabled={setAutoExit} />
                <ToggleSwitch label="Dark Mode" enabled={darkMode} setEnabled={setDarkMode} />
                <ToggleSwitch label="System Algo Trade" enabled={platformAlgoTrade} setEnabled={handlePlatformAlgoTradeToggle} />
                <button onClick={() => navigate('/settings/automation')} className="flex items-center space-x-2 text-sm font-semibold text-blue-600 hover:text-blue-800">
                    <LuSlidersHorizontal size={16} />
                    <span>Automation Rules</span>
                </button>
            </div>

            {/* Account, App, and Support in one card for a cleaner look */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 divide-y divide-slate-100">
                <div className="p-5">
                    <h3 className="text-lg font-bold text-slate-800 mb-2">Account</h3>
                    <SettingsItem icon={<LuKey className="text-slate-500" size={20} />} text="Change Password" onClick={() => setIsChangePasswordModalOpen(true)} />
                    <SettingsItem
                        icon={<LuWebhook className="text-slate-500" size={20} />}
                        text="TradingView Webhook"
                        onClick={() => setIsWebhookModalOpen(true)}
                    />
                </div>
                <div className="p-5">
                    <h3 className="text-lg font-bold text-slate-800 mb-2">App</h3>
                    <SettingsItem icon={<LuBell className="text-slate-500" size={20} />} text="Notifications" onClick={() => { }} />
                    <SettingsItem icon={<LuDownload className="text-slate-500" size={20} />} text="Export Data" onClick={() => { }} />
                    <SettingsItem icon={<LuTrash2 className="text-slate-500" size={20} />} text="Clear Trade History" onClick={() => { }} />
                </div>
                <div className="p-5">
                    <h3 className="text-lg font-bold text-slate-800 mb-2">Support</h3>
                    <SettingsItem icon={<LuCircleHelp className="text-slate-500" size={20} />} text="Help & FAQ" onClick={() => { }} />
                    <SettingsItem icon={<LuMail className="text-slate-500" size={20} />} text="Contact Support" onClick={() => { }} />
                    <SettingsItem icon={<LuInfo className="text-slate-500" size={20} />} text="About" onClick={() => { }} />
                </div>
            </div>

            {/* Danger Zone */}
            <div className="pt-2">
                <button
                    onClick={handleLogout}
                    className="w-full flex items-center justify-center p-3 text-red-600 bg-red-100 rounded-lg hover:bg-red-200 transition-colors font-semibold"
                >
                    <LuLogOut className="mr-2" />
                    <span>Logout</span>
                </button>
            </div>

            {/* Modal for Webhook URL */}
            <Modal isOpen={isWebhookModalOpen} onClose={() => setIsWebhookModalOpen(false)}>
                <WebhookModal onClose={() => setIsWebhookModalOpen(false)} />
            </Modal>

            {/* Modal for Change Password */}
            <Modal isOpen={isChangePasswordModalOpen} onClose={() => setIsChangePasswordModalOpen(false)}>
                <ChangePasswordModal onClose={() => setIsChangePasswordModalOpen(false)} />
            </Modal>
        </div>
    );
};

export default SettingsPage;
